import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import { get } from 'lodash';
import { coreApi, screeningsApiClient } from 'src/api/config';
import { useCurrentEmployeeId } from 'src/common/contexts/CurrentEmployeeContext';

export const TASKS_QUERY_KEYS = {
  CRON_SYNC: 'tasks-cron-sync',
  TASKS: 'tasks',
  SOCIAL_NEEDS: 'tasks-social-needs',
  ENHANCED_POPULATIONS: 'tasks-enhanced-populations',
  SUBMITTING_PROVIDERS: 'tasks-submitting-providers',
  ADDRESS_COUNTIES: 'tasks-address-counties',
  TASK_SEND_ORGANIZATIONS: 'tasks-send-organizations',
  CURRENT_PROVIDER_CUSTOMER_INITIATIVE_PROVIDERS: 'tasks-current-provider-customer-initiative-providers',
};
export const TASKS_PAGE_SIZE = 25;

const DUE_DATE_RANGE_UNITS = {
  today: 'day',
  this_week: 'week',
  this_month: 'month',
  this_year: 'year',
};
const INITIATIVE_CONDITIONS_PAGE = { number: 1, size: 100 };
const TASK_RELATIONSHIPS = [
  ['person', 'person'],
  ['assignee_employee', 'employee'],
  ['assignee_provider', 'provider'],
  ['submitter_employee', 'employee'],
  ['submitter_provider', 'provider'],
];

const getDueDateRangeFilters = (dueDateRange) => {
  if (!dueDateRange || dueDateRange === 'all_dates') {
    return {};
  }

  const unit = DUE_DATE_RANGE_UNITS[dueDateRange];
  if (!unit) {
    return {};
  }

  const now = moment();
  const start = now.clone().startOf(unit);
  const end = now.clone().endOf(unit);

  return {
    due_date_start: start.format('YYYY-MM-DD'),
    due_date_end: end.format('YYYY-MM-DD'),
  };
};

const getCountyFilterName = (countyValue) => countyValue?.split('::')[1] || countyValue;
const getSingleFilterValue = (values = []) => values[0];
const isEnhancedPopulationCondition = (condition) => condition?.esmf_type === 'enhanced_population';

const normalizeRecords = (records) => (Array.isArray(records) ? records : [records]);
const hasPersonAddresses = (person) => Boolean(person?.addresses?.length);
const getAddressesData = (response) => response?.data?.data ?? [];
const getRecordLanguagesData = (response) => response?.data?.data ?? [];
const getInitiativeConditionsData = (response) => response?.data?.data ?? [];

const fetchPersonData = async (person) => {
  const [addressesResponse, languagesResponse] = await Promise.all([
    hasPersonAddresses(person) ? coreApi.query('address', { person: person.id }) : Promise.resolve(null),
    coreApi.query('record_language', { record_id: person.id, record_type: 'Person' }),
  ]);

  return {
    ...(addressesResponse ? { addresses: getAddressesData(addressesResponse) } : {}),
    record_languages: getRecordLanguagesData(languagesResponse),
  };
};

const fetchTaskPersonInitiativeConditions = async (personId) => {
  try {
    const response = await coreApi.query(
      `people/${personId}/initiative_conditions`,
      { is_active: true },
      INITIATIVE_CONDITIONS_PAGE,
    );

    return getInitiativeConditionsData(response).filter(isEnhancedPopulationCondition);
  } catch (_error) {
    return [];
  }
};

const populateTaskPersonInitiativeConditions = async (records) => {
  const personIds = Array.from(
    new Set(normalizeRecords(records).map((record) => record?.person?.id).filter(Boolean)),
  );

  if (!personIds.length) {
    return;
  }

  const initiativeConditionsByPersonId = new Map(
    await Promise.all(personIds.map(async (personId) => (
      [personId, await fetchTaskPersonInitiativeConditions(personId)]
    ))),
  );

  normalizeRecords(records).forEach((record) => {
    if (!record?.person?.id) {
      return;
    }

    Object.assign(record.person, {
      initiative_conditions: initiativeConditionsByPersonId.get(record.person.id) ?? [],
    });
  });
};

const populateScreeningTaskPersonMetadata = async (records) => {
  await Promise.all(normalizeRecords(records).map(async (record) => {
    const person = record?.person;
    if (!person?.id) {
      return;
    }

    Object.assign(person, await fetchPersonData(person));
  }));
};

const buildTaskQueryFilters = ({
  provider,
  assignee,
  status,
  county,
  language,
  submitterIds,
  subjectType,
  identifiedNeeds,
  identifiedNeedsCount,
  enhancedPopulations,
  dueDateRange,
}) => {
  const queryFilters = {};

  if (assignee) {
    queryFilters.assignee_employee_id = assignee;
  }
  if (provider) {
    queryFilters.assignee_provider_id = provider;
  }
  if (status) {
    queryFilters.status = status;
  }
  if (county?.length) {
    queryFilters.county = [...new Set(county.map(getCountyFilterName).filter(Boolean))].join(',');
  }
  if (language?.length) {
    queryFilters.language = getSingleFilterValue(language);
  }
  if (submitterIds?.length) {
    queryFilters.submitter_provider_id = [...new Set(submitterIds)].join(',');
  }
  if (subjectType) {
    queryFilters.subject_type = subjectType;
  }
  if (identifiedNeeds?.length) {
    queryFilters.screening_identified_needs = [...new Set(identifiedNeeds)].join(',');
  }
  if (identifiedNeedsCount?.length) {
    queryFilters.screening_identified_needs_count = [...new Set(identifiedNeedsCount)].join(',');
  }
  if (enhancedPopulations?.length) {
    queryFilters['person.initiative_conditions'] = [...new Set(enhancedPopulations)].join(',');
  }

  return {
    ...queryFilters,
    ...getDueDateRangeFilters(dueDateRange),
  };
};

const buildTaskQueryParams = ({ page, subjectType, sort }) => ({
  page: { number: page, size: TASKS_PAGE_SIZE },
  ...(subjectType === 'Screening' ? { include: 'task_reference' } : {}),
  ...(sort ? { sort } : {}),
});

const populateTaskRelationships = async (tasks) => {
  await Promise.allSettled(
    TASK_RELATIONSHIPS.map(([relationshipName, type]) => (
      coreApi.populateRelationship(relationshipName, type, tasks)
    )),
  );
};

export const useTasks = (filters = {}, queryOptions = {}) => {
  const {
    provider,
    assignee,
    status,
    county,
    language,
    submitterIds,
    subjectType,
    identifiedNeeds,
    identifiedNeedsCount,
    enhancedPopulations,
    dueDateRange,
    sort,
    page = 1,
  } = filters;

  const queryFilters = buildTaskQueryFilters({
    provider,
    assignee,
    status,
    county,
    language,
    submitterIds,
    subjectType,
    identifiedNeeds,
    identifiedNeedsCount,
    enhancedPopulations,
    dueDateRange,
  });
  const queryParams = buildTaskQueryParams({ page, subjectType, sort });

  return useQuery(
    [TASKS_QUERY_KEYS.TASKS, queryFilters, queryParams],
    async () => {
      const response = await coreApi.query('tasks', queryFilters, queryParams);

      const data = response?.data ?? { data: [], meta: {} };
      const tasks = data.data ?? [];
      try {
        await populateTaskRelationships(tasks);
      } catch (_error) {
        // Best-effort enrichment should never block task data.
      }
      try {
        await populateTaskPersonInitiativeConditions(tasks);
      } catch (_error) {
        // Best-effort enrichment should never block task data.
      }
      if (subjectType === 'Screening') {
        try {
          await populateScreeningTaskPersonMetadata(tasks);
        } catch (_error) {
          // Best-effort enrichment should never block task data.
        }
      }
      return data;
    },
    {
      enabled: !!provider,
      ...queryOptions,
    },
  );
};

export const useTaskById = (taskId, queryOptions = {}) => useQuery(
  [TASKS_QUERY_KEYS.TASKS, taskId],
  async () => {
    const response = await coreApi.findRecord('task', taskId);
    const task = response?.data?.data;
    if (task) {
      await populateTaskRelationships(task);
      await populateTaskPersonInitiativeConditions(task);
    }
    return task ?? null;
  },
  {
    enabled: !!taskId,
    ...queryOptions,
  },
);

export const useCreateTask = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    (taskData) => coreApi.createRecord('task', taskData),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(TASKS_QUERY_KEYS.TASKS);
        if (onSuccess) {
          onSuccess(...args);
        }
      },
    },
  );
};

export const useTasksCron = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ jobType }) => coreApi.updateRecord('task', 'cron', { job_type: jobType }),
    {
      mutationKey: TASKS_QUERY_KEYS.CRON_SYNC,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(TASKS_QUERY_KEYS.TASKS);
        if (onSuccess) {
          onSuccess(...args);
        }
      },
    },
  );
};

export const useSocialNeeds = (queryOptions = {}) => useQuery(
  [TASKS_QUERY_KEYS.SOCIAL_NEEDS],
  async ({ signal }) => {
    const response = await screeningsApiClient.get('/social_needs', { signal });
    const socialNeeds = get(response, 'data.social_needs', []);

    return socialNeeds
      .map((need) => ({
        value: need.id,
        label: need.name,
      }))
      .filter((need) => need.value && need.label);
  },
  {
    retry: false,
    ...queryOptions,
  },
);

export const useEnhancedPopulations = (queryOptions = {}) => useQuery(
  [TASKS_QUERY_KEYS.ENHANCED_POPULATIONS],
  async ({ signal }) => {
    const response = await coreApi.query(
      'initiative_conditions',
      {
        is_active: true,
        initiative_name: 'NYHER 1115 Waiver',
      },
      { signal },
    );

    const initiativeConditions = get(response, 'data.data', []);
    return initiativeConditions
      .filter(isEnhancedPopulationCondition)
      .map((condition) => ({
        value: condition.id,
        label: condition.description,
      }));
  },
  {
    retry: false,
    ...queryOptions,
  },
);

export const useTasksSubmittingProviders = (filters = {}, queryOptions = {}) => {
  const currentEmployeeId = useCurrentEmployeeId();
  return useQuery(
    [TASKS_QUERY_KEYS.SUBMITTING_PROVIDERS, currentEmployeeId, filters],
  async ({ signal }) => {
    const response = await coreApi.query(
      'provider',
      {
        ...filters,
        'tasks.submitter_providers': true,
      },
      { signal },
    );

    return { data: get(response, 'data.data', []) };
  },
  {
    retry: false,
    enabled: !!currentEmployeeId && (queryOptions.enabled ?? true),
    ...queryOptions,
  },
  );
};

export const useBatchAssignTasks = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ taskIds, assigneeProviderId, assigneeEmployeeId }) => coreApi.updateRecord(
      'task',
      'batch',
      {
        task_ids: taskIds,
        assignee_provider_id: assigneeProviderId,
        ...(assigneeEmployeeId ? { assignee_employee_id: assigneeEmployeeId } : {}),
      },
    ),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(TASKS_QUERY_KEYS.TASKS);
        if (onSuccess) {
          onSuccess(...args);
        }
      },
    },
  );
};

export const useBatchUpdateTaskStatus = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ taskIds, status }) => coreApi.updateRecord(
      'task',
      'batch',
      {
        task_ids: taskIds,
        status,
      },
    ),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(TASKS_QUERY_KEYS.TASKS);
        if (onSuccess) {
          onSuccess(...args);
        }
      },
    },
  );
};

// export const useCurrentProviderCustomerInitiatives = (
//   currentProviderId,
//   queryOptions = {},
// ) => useQuery(
//   [TASKS_QUERY_KEYS.CURRENT_PROVIDER_CUSTOMER_INITIATIVE_PROVIDERS, currentProviderId],
//   async ({ signal }) => {
//     const customerInitiativesResponse = await coreApi.query(
//       'customer_initiative',
//       { provider: currentProviderId },
//       { signal },
//     );
//     const customerInitiatives = get(customerInitiativesResponse, 'data.data', []);

//     return [...new Set(
//       customerInitiatives
//         .map((customerInitiative) => get(customerInitiative, 'name'))
//         .filter(Boolean),
//     )];
//   },
//   {
//     enabled: !!currentProviderId,
//     retry: false,
//     ...queryOptions,
//   },
// );

export const useFetchTaskSendOrganizations = (currentProviderId, leadSCNFlag) => useQuery(
  [TASKS_QUERY_KEYS.TASK_SEND_ORGANIZATIONS, currentProviderId, leadSCNFlag],
  async ({ signal }) => {
    const leadProviders = leadSCNFlag?.leadProviders ?? [];
    const scnLeadConfig = leadProviders.find((provider) => provider?.providerId === currentProviderId) ?? null;

    let providerIdsToFetch = [];

    if (scnLeadConfig) {
      const initiativeIds = [...new Set(scnLeadConfig.customerInitiatives?.filter(Boolean) ?? [])];

      if (initiativeIds.length > 0) {
        const cipsResponse = await coreApi.query(
          'customer_initiative_providers',
          { customer_initiative_id: initiativeIds.join(',') },
          { signal },
        );
        const cips = get(cipsResponse, 'data.data', []);

        providerIdsToFetch = [...new Set(
          cips
            .map((cip) => get(cip, 'provider.id', get(cip, 'provider_id')))
            .filter(Boolean),
        )];
      }
    } else {
      const myCipsResponse = await coreApi.query(
        'customer_initiative_providers',
        { provider_id: currentProviderId },
        { signal },
      );
      const myCips = get(myCipsResponse, 'data.data', []);
      const myCustomerInitiativeIds = [...new Set(
        myCips
          .map((cip) => get(cip, 'customer_initiative.id', get(cip, 'customer_initiative_id')))
          .filter(Boolean),
      )];

      providerIdsToFetch = [...new Set(
        leadProviders
          .filter((provider) => provider?.customerInitiatives?.some((id) => myCustomerInitiativeIds.includes(id)))
          .map((provider) => provider?.providerId)
          .filter(Boolean),
      )];
    }

    if (providerIdsToFetch.length === 0) {
      return [];
    }

    const providersResponse = await coreApi.query(
      'provider',
      { id: providerIdsToFetch.join(',') },
      { signal },
    );
    const providers = get(providersResponse, 'data.data', []);

    return providers.map((provider) => ({
      value: provider.id,
      label: get(provider, 'name', provider.id),
    }));
  },
  {
    enabled: !!currentProviderId,
    retry: false,
  },
);

/**
 * Derives org select options and radio state for the ReassignTasksModal.
 * - SCN lead: options = all providers across the lead's customer initiatives
 * - Non-SCN lead: options = lead providers for matching customer initiatives
 */
export const useTaskSendOrganizations = (currentProviderId, leadSCNFlag) => {
  const { data: orgOptions = [], isLoading } = useFetchTaskSendOrganizations(currentProviderId, leadSCNFlag);
  const orgRadioDisabled = orgOptions.length === 0;
  const leadProviders = leadSCNFlag?.leadProviders ?? [];
  const isScnLeadProvider = leadProviders.some((provider) => provider?.providerId === currentProviderId);

  return {
    orgOptions,
    isLoading,
    orgRadioDisabled,
    isScnLeadProvider,
  };
};
